import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    Box,
    Button,
    Menu,
    MenuItem,
    InputAdornment,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
    alpha,
} from '@mui/material';
import ResponsiveTooltip from '../ResponsiveTooltip';
import React, { useMemo, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useFormatter } from '../../context/FormatterContext';
import { getDisplayCoin } from '../../utils/displayFormatters';
import AssetDistributionModal from './AssetDistributionModal';
import FilledTableSortLabel from '../common/FilledTableSortLabel';
import StyledPagination from '../common/StyledPagination';

export interface AccountDistribution {
    exchangeName: string;
    quantity: number;
    imported_quantity: number;
    unit_price: number;
    value: number;
    allocationPercentage: number;
    hasBalanceDiscrepancy: boolean;
}

interface Asset {
    name: string;
    symbol: string;
    displaySymbol: string;
    quantity: number;
    price: number;
    value: number;
    percentageOfTotalValue: number;
    price_trend1: number;
    price_trend7: number;
    price_trend30: number;
    image: string;
    accountDistributions: AccountDistribution[];
}

interface AssetTableProps {
    assets: Asset[];
    isLoading?: boolean;
}

type Order = 'asc' | 'desc';
type OrderBy = keyof Asset;

type TimeFrame = '24h' | '7d' | '30d';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order: Order, orderBy: OrderBy): (a: Asset, b: Asset) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
    id: OrderBy;
    label: string;
    numeric: boolean;
}

const createHeadCells = (t: (key: string) => string): readonly HeadCell[] => [
    { id: 'name', label: t('dashboard.assetTable.columns.name'), numeric: false },
    { id: 'quantity', label: t('dashboard.assetTable.columns.quantity'), numeric: true },
    { id: 'price', label: t('dashboard.assetTable.columns.price'), numeric: true },
    { id: 'value', label: t('dashboard.assetTable.columns.value'), numeric: true },
    { id: 'price_trend1', label: t('dashboard.assetTable.columns.performance'), numeric: true },
];

const AssetTable: React.FC<AssetTableProps> = ({ assets, isLoading = false }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const headCells = useMemo(() => createHeadCells(t), [t]);
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<OrderBy>('value');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [timeFrame, setTimeFrame] = useState<TimeFrame>('24h');
    const [timeFrameAnchorEl, setTimeFrameAnchorEl] = useState<null | HTMLElement>(null);
    
    const { formatPercentage, formatSmartQuantity, formatSmartPrice, formatSmartCurrency, formatWithTooltip } =
        useFormatter();

    const handleRequestSort = (property: OrderBy) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenModal = (asset: Asset) => {
        setSelectedAsset(asset);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleTimeFrameClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setTimeFrameAnchorEl(event.currentTarget);
    };
    
    const handleTimeFrameClose = () => {
        setTimeFrameAnchorEl(null);
    };
    
    const handleTimeFrameSelect = (timeFrame: '24h' | '7d' | '30d') => (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setTimeFrame(timeFrame);
        handleTimeFrameClose();
    };

    const getPriceTrend = (asset: Asset): number => {
        switch (timeFrame) {
            case '24h':
                return asset.price_trend1;
            case '7d':
                return asset.price_trend7;
            case '30d':
                return asset.price_trend30;
            default:
                return asset.price_trend1;
        }
    };

    const getValueChange = (asset: Asset): number => {
        const priceTrend = getPriceTrend(asset);
        return asset.value * priceTrend / 100;
    };

    const filteredAssets = useMemo(() => {
        return assets.filter(
            (asset) =>
                asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                asset.symbol.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [assets, searchTerm]);

    const sortedAssets = useMemo(() => {
        return [...filteredAssets].sort(getComparator(order, orderBy));
    }, [filteredAssets, order, orderBy]);

    const paginatedAssets = useMemo(() => {
        return sortedAssets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [sortedAssets, page, rowsPerPage]);

    const SkeletonRows = () => {
        return (
            <>
                {[...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                                <Box>
                                    <Skeleton width={100} height={24} />
                                    <Skeleton width={60} height={18} />
                                </Box>
                            </Box>
                        </TableCell>
                        <TableCell align="left"><Skeleton width={80} height={24} /></TableCell>
                        <TableCell align="left"><Skeleton width={80} height={24} /></TableCell>
                        <TableCell align="left"><Skeleton width={80} height={24} /></TableCell>
                        <TableCell align="left"><Skeleton width={80} height={24} /></TableCell>
                    </TableRow>
                ))}
            </>
        );
    };

    return (
        <Box
            sx={{
                pt: 1.5,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: 1,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
            }}
        >
            <Box sx={{
                p: 2,
                px: 4,
                display: 'flex',
                width: '100%',
                alignItems: 'center',
            }}>
                <TextField
                    placeholder={t('dashboard.assetTable.search')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        width: '100%',
                    }}
                />
            </Box>
            <TableContainer sx={{ px: 4 }}>
                <Table aria-labelledby="tableTitle" size="medium" sx={{
                    minWidth: 750,
                    tableLayout: 'fixed',
                    borderCollapse: 'collapse',
                    width: '100%',
                    '& .MuiTableCell-root': {
                        py: 1.5,
                        borderBottom: '2px solid',
                        borderBottomColor: `${theme.palette.divider}`,
                        borderBottomOpacity: 0.6,
                    },
                    '& .MuiTableHead-root .MuiTableCell-root': {
                        bgcolor: 'background.paper',
                        fontWeight: 600,
                        borderBottom: '2px solid',
                        borderBottomColor: `${theme.palette.divider}`,
                    },
                    // Column width distribution
                    '& .MuiTableCell-root:nth-of-type(1)': { width: '25%', minWidth: '180px' },  // Name column
                    '& .MuiTableCell-root:nth-of-type(2)': { width: '18%', minWidth: '100px' },  // Quantity
                    '& .MuiTableCell-root:nth-of-type(3)': { width: '18%', minWidth: '100px' },  // Price
                    '& .MuiTableCell-root:nth-of-type(4)': { width: '18%', minWidth: '100px' },  // Value
                    '& .MuiTableCell-root:nth-of-type(5)': { width: '21%', minWidth: '120px' },  // Performance
                }}>
                    <TableHead>
                        <TableRow>
                            {headCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id}
                                    align="left"
                                    padding="normal"
                                    sortDirection={orderBy === headCell.id ? order : false}
                                >
                                    <FilledTableSortLabel
                                        active={orderBy === headCell.id}
                                        direction={orderBy === headCell.id ? order : 'asc'}
                                        onClick={() => handleRequestSort(headCell.id)}
                                    >
                                        {headCell.id === 'price_trend1' ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body2" sx={{ mr: 1 }}>
                                                    {t('dashboard.assetTable.columns.performance')}
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={handleTimeFrameClick}
                                                    endIcon={<KeyboardArrowDownIcon />}
                                                    sx={{
                                                        minWidth: 0,
                                                        px: 1,
                                                        py: 0.5,
                                                        borderColor: theme.palette.divider,
                                                        color: theme.palette.text.primary,
                                                        '&:hover': {
                                                            borderColor: theme.palette.divider,
                                                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                                                        },
                                                    }}
                                                >
                                                    {t(`dashboard.assetTable.timeframes.${timeFrame}`)}
                                                </Button>
                                                <Menu
                                                    anchorEl={timeFrameAnchorEl}
                                                    open={Boolean(timeFrameAnchorEl)}
                                                    onClose={handleTimeFrameClose}
                                                    onClick={(e) => e.stopPropagation()}
                                                    PaperProps={{
                                                        sx: {
                                                            bgcolor: (theme) => theme.palette.background.paper,
                                                            borderRadius: 2,
                                                            border: '1px solid',
                                                            borderColor: (theme) => theme.palette.divider,
                                                            boxShadow: (theme) => `0px 5px 15px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                                                        }
                                                    }}
                                                >
                                                    <MenuItem 
                                                        onClick={handleTimeFrameSelect('24h')}
                                                        selected={timeFrame === '24h'}
                                                        sx={{ minWidth: '100px' }}
                                                    >
                                                        {t('dashboard.assetTable.timeframes.24h')}
                                                    </MenuItem>
                                                    <MenuItem 
                                                        onClick={handleTimeFrameSelect('7d')}
                                                        selected={timeFrame === '7d'}
                                                        sx={{ minWidth: '100px' }}
                                                    >
                                                        {t('dashboard.assetTable.timeframes.7d')}
                                                    </MenuItem>
                                                    <MenuItem 
                                                        onClick={handleTimeFrameSelect('30d')}
                                                        selected={timeFrame === '30d'}
                                                        sx={{ minWidth: '100px' }}
                                                    >
                                                        {t('dashboard.assetTable.timeframes.30d')}
                                                    </MenuItem>
                                                </Menu>
                                            </Box>
                                        ) : headCell.label}
                                        {orderBy === headCell.id ? (
                                            <Box component="span" sx={{ display: 'none' }}>
                                                {order === 'desc' ? t('common.table.sort.descending') : t('common.table.sort.ascending')}
                                                </Box>
                                        ) : null}
                                    </FilledTableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <SkeletonRows />
                        ) : (
                            paginatedAssets.map((asset) => (
                                <TableRow
                                    hover
                                    key={asset.symbol}
                                    onClick={() => handleOpenModal(asset)}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:last-child td, &:last-child th': {
                                            border: 0,
                                        },
                                    }}
                                >
                                    <TableCell component="th" scope="row">
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}>
                                            <img
                                                src={`https://cointracking.info/img/coins/${asset.image}`}
                                                loading="lazy"
                                                alt={asset.name}
                                                style={{
                                                    width: 24,
                                                    height: 24,
                                                    marginRight: 8,
                                                    borderRadius: '50%',
                                                }}
                                            />
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{asset.name}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {getDisplayCoin(asset.displaySymbol, asset.symbol)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="left">
                                        <ResponsiveTooltip title={formatWithTooltip(asset.quantity).tooltip}>
                                            <Typography sx={{ fontWeight: 'bold' }}>
                                                {formatSmartQuantity(asset.quantity)}
                                            </Typography>
                                        </ResponsiveTooltip>
                                    </TableCell>
                                    <TableCell align="left">
                                        <ResponsiveTooltip title={formatWithTooltip(asset.price).tooltip}>
                                            <Typography sx={{ fontWeight: 'bold' }}>
                                                {formatSmartPrice(asset.price)}
                                            </Typography>
                                        </ResponsiveTooltip>
                                    </TableCell>
                                    <TableCell align="left">
                                        <ResponsiveTooltip title={formatWithTooltip(asset.value).tooltip}>
                                            <Typography sx={{ fontWeight: 'bold' }}>
                                                {formatSmartCurrency(asset.value)}
                                            </Typography>
                                        </ResponsiveTooltip>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                        }}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: getPriceTrend(asset) >= 0 
                                                        ? '#FFFFFF'
                                                        : '#FFFFFF',
                                                    fontWeight: 'bold',
                                                    bgcolor: getPriceTrend(asset) >= 0
                                                        ? '#15B834'
                                                        : '#D32F2F',
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    display: 'inline-block',
                                                }}
                                            >
                                                {getPriceTrend(asset) >= 0 ? '+' : ''}{formatPercentage(getPriceTrend(asset))}
                                            </Typography>
                                            
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: getPriceTrend(asset) >= 0 
                                                        ? '#15B834'
                                                        : '#D32F2F',
                                                    mt: 0.5,
                                                }}
                                            >
                                                {getPriceTrend(asset) >= 0 ? '+' : ''}{formatSmartCurrency(getValueChange(asset))}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <StyledPagination
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage={t('common.table.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('common.table.pagination.of')} ${count}`}
                count={sortedAssets.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            {selectedAsset && (
                <AssetDistributionModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    assetName={selectedAsset.name}
                    assetSymbol={selectedAsset.symbol}
                    assetTicker={selectedAsset.displaySymbol}
                />
            )}
        </Box>
    );
};

export default AssetTable;
